/**
 * TripCurrencyBadge — iter191.
 * Sits just under the TripHero on the Overview tab.
 *
 * Three states (Trip groups only):
 *   1. Not set up + caller is admin       → "Set up multi-currency" CTA pill
 *   2. Not set up + caller is NOT admin   → renders nothing (no clutter)
 *   3. Set up                              → "🔒 1 € = ₹92" locked badge
 *
 * Visible on the Trip Overview tab regardless of `trip_rate_locked` — even
 * before the first expense, displaying the badge tells members "this trip is
 * EUR-based" which is useful context.
 */
import { Lock, ArrowRightLeft } from 'lucide-react';
import { CURRENCY_SYMBOLS } from '../lib/formatNXC';

export default function TripCurrencyBadge({ group, isAdmin, onSetupClick }) {
  if (group?.group_type !== 'trip') return null;

  const home = group?.currency || 'INR';
  const trip = group?.trip_currency;
  const rate = group?.trip_rate;
  const homeSym = CURRENCY_SYMBOLS[home] || home;

  // Not set up yet.
  if (!trip || !rate) {
    if (!isAdmin) return null;
    return (
      <button
        type="button"
        onClick={onSetupClick}
        data-testid="set-trip-rate-cta"
        className="w-full rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 px-4 py-2.5 flex items-center justify-between hover:bg-primary/10 active:scale-[0.99] transition text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <ArrowRightLeft className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[12px] font-bold text-primary">Set up multi-currency</p>
            <p className="text-xs text-muted-foreground truncate">For trips abroad — lock the rate once.</p>
          </div>
        </div>
        <span className="text-xs font-bold text-primary shrink-0">Set →</span>
      </button>
    );
  }

  // Set up — show locked badge.
  const tripSym = CURRENCY_SYMBOLS[trip] || trip;
  return (
    <div
      className="w-full rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-2 flex items-center justify-between"
      data-testid="trip-rate-locked-badge"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Lock className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300 shrink-0" />
        <p className="text-[12px] font-bold text-amber-900 dark:text-amber-100 tabular-nums truncate">
          1 {tripSym} = {homeSym}{Number(rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
        </p>
        <span className="text-amber-400 text-[12px]">·</span>
        <p className="text-xs text-amber-800/80 dark:text-amber-200/80 shrink-0">locked</p>
      </div>
    </div>
  );
}
